import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap'
import { Download, Database, Brain } from 'lucide-react'
import styled from 'styled-components'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { useCreateExportMutation } from '../../../services/exportApi'
import ExportsSubView from '../subviews/ExportsSubView'
import DatasetExportModal from '../../modals/DatasetExportModal'
import SelectModelModal from '../../modals/SelectModelModal'
import ErrorDialog from '../../modals/ErrorDialog'
import { ExportType } from '../../../types/export'

const ExportContainer = styled.div`
  background: #1a1a1a;
  min-height: 100vh;
  padding: 2rem;
`

const ExportCard = styled(Card)`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  transition: all 0.3s ease;
  height: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    border-color: rgba(102, 126, 234, 0.4);
  }

  .card-body {
    padding: 2rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }

  .card-title {
    color: #ffffff;
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  .card-text {
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 1.5rem;
    flex-grow: 1;
  }
`

const SectionCard = styled(Card)`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
`

const IconWrapper = styled.div`
  color: #800085;
  margin-bottom: 1rem;
`

const ExportView: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const dispatch = useAppDispatch()
  const [showDatasetModal, setShowDatasetModal] = useState(false)
  const [showModelModal, setShowModelModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const [createExport] = useCreateExportMutation()

  const handleDatasetExport = () => {
    setShowDatasetModal(true)
  }

  const handleModelExport = () => {
    setShowModelModal(true)
  }

  // Bulk export currently not exposed as a top-level action; users can select multiple types in the dialog if supported.

  const createDatasetExport = async (exportName: string): Promise<void> => {
    try {
      if (!project?.id || !project?.datasetid) {
        throw new Error('Project ID or Dataset ID is missing')
      }

      await createExport({
        name: exportName,
        export_types: ['DATASET'],
        project_id: project.id,
        dataset_id: project.datasetid,
        model_id: undefined,
      })
      setShowDatasetModal(false)
    } catch (err) {
      setError(
        err
          ? (err as any).data?.message || 'Failed to create export'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    }
  }

  const handleExportClick = (type: 'dataset' | 'model') => {
    switch (type) {
      case 'dataset':
        handleDatasetExport()
        break
      case 'model':
        handleModelExport()
        break
    }
  }

  const exportOptions = [
    {
      title: 'Dataset Export',
      description: 'Export all project datasets with COCO‑formatted labels.',
      icon: <Database size={32} />,
      type: 'dataset' as const,
      available: true,
    },
    {
      title: 'Model Export',
      description: 'Export trained models in YOLO format.',
      icon: <Brain size={32} />,
      type: 'model' as const,
      available: true,
    },
  ]

  return (
    <>
      <Container fluid>
        <Row>
          {exportOptions.map((option, index) => (
            <Col key={index} md={6} lg={6} className="mb-4">
              <ExportCard
                style={{
                  opacity: option.available ? 1 : 0.6,
                  cursor: option.available ? 'pointer' : 'not-allowed',
                }}
              >
                <div className="card-body">
                  <div>
                    <IconWrapper>{option.icon}</IconWrapper>
                    <h5 className="card-title">{option.title}</h5>
                    <p className="card-text">{option.description}</p>
                    {option.type === 'model' && (
                      <Alert
                        variant="warning"
                        style={{
                          background: 'rgba(245, 158, 11, 0.1)',
                          border: '1px solid rgba(245, 158, 11, 0.3)',
                          color: '#fbbf24',
                          fontSize: '0.875rem',
                        }}
                        className="mt-2"
                      >
                        YOLO exports are provided via Ultralytics. By exporting, you acknowledge and
                        agree to the Ultralytics license terms. See{' '}
                        <a
                          href="https://ultralytics.com/license"
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#fde68a', textDecoration: 'underline' }}
                        >
                          ultralytics.com/license
                        </a>
                        .
                      </Alert>
                    )}
                  </div>
                  {option.available ? (
                    <Button
                      variant="primary"
                      onClick={() => handleExportClick(option.type)}
                      style={{
                        background: '#800085',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.75rem 1.5rem',
                        fontWeight: '500',
                      }}
                    >
                      <Download size={16} className="me-2" />
                      Export
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      disabled
                      style={{
                        borderRadius: '8px',
                        padding: '0.75rem 1.5rem',
                        fontWeight: '500',
                        opacity: 0.5,
                      }}
                    >
                      <Download size={16} className="me-2" />
                      Unavailable
                    </Button>
                  )}
                </div>
              </ExportCard>
            </Col>
          ))}
        </Row>

        <Row className="mt-5">
          <Col md={12}>
            <SectionCard>
              <Card.Body>
                <h5 style={{ color: '#ffffff', marginBottom: '1rem' }}>Export History</h5>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1.5rem' }}>
                  View, download, and manage your previous exports.
                </p>
                {/* Inline history view */}
                <ExportsSubView isModal={false} />
              </Card.Body>
            </SectionCard>
          </Col>
        </Row>
      </Container>

      {/* Dataset export modal */}
      <DatasetExportModal
        show={showDatasetModal}
        onHide={() => setShowDatasetModal(false)}
        projectName={project?.name}
        onConfirm={(name) => createDatasetExport(name)}
      />

      {/* Model selection modal */}
      <SelectModelModal
        show={showModelModal}
        onHide={() => setShowModelModal(false)}
        onModelSelect={async (model) => {
          try {
            if (!project?.id || !project?.datasetid) {
              throw new Error('Project ID or Dataset ID is missing')
            }
            await createExport({
              name: `${project.name}-${model.name}-${new Date().toLocaleDateString('en-US', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}`,
              export_types: ['MODEL'],
              project_id: project.id,
              dataset_id: project.datasetid,
              model_id: model.id,
            })
            setShowModelModal(false)
          } catch (err) {
            setError(
              err
                ? (err as any).data?.message || 'Failed to create export'
                : 'An unexpected error occurred'
            )
            setShowError(true)
          }
        }}
      />

      {/* Exports Management Modal removed; history is inline */}

      {/* Error Dialog */}
      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={error} />
    </>
  )
}

export default ExportView
